import React, { useEffect, useState } from 'react';

import { Button, Popover, TextFieldProps } from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFnsV3';
import { endOfDay, getTime, isValid } from 'date-fns';
import { useDebounce } from 'hooks/useDebounce';
import { useTypedTranslate } from 'providers/i18n/hooks/useTypedTranslate';
import { DateObject } from 'react-multi-date-picker';
import { Calendar } from 'shared/mui/Calendar/Calendar';
import { DatePickerActions } from 'shared/mui/DatePicker/components/DatePickerActions/DatePickerActions';
import { DatePickerMaskInput } from 'shared/mui/DatePicker/components/DatePickerMaskInput/DatePickerMaskInput';
import {
  DEFAULT_SHORTCUTS,
  Shortcuts,
} from 'shared/mui/DatePicker/components/DatePickerShortcuts/constants/shortcuts';
import { DatePickerShortcuts } from 'shared/mui/DatePicker/components/DatePickerShortcuts/DatePickerShortcuts';
import { DatePickerTime } from 'shared/mui/DatePicker/components/DatePickerTime/DatePickerTime';
import { DatePickerToolbar } from 'shared/mui/DatePicker/components/DatePickerToolbar/DatePickerToolbar';
import {
  DEFAULT_DATE_MASK,
  DEFAULT_DATE_TIME_MASK,
  DEFAULT_DATE_TIME_RANGE_MASK,
  DEFAULT_DATE_TIME_RANGE_PLACEHOLDER,
  DEFAULT_SEPARATOR,
  DEFAULT_TIME_MASK,
} from 'shared/mui/DatePicker/config/defaultValues';
import {
  PopoverCalendarContainerStyled,
  PopoverContentStyled,
  PopoverDatesStyled,
  PopoverLayoutStyled,
  PopoverListStyled,
  PopoverTimesStyled,
} from 'shared/mui/DatePicker/DatePicker.styled';
import { useAnchor } from 'shared/mui/DatePicker/hooks/useAnchor';
import { formatRangeInputToPlaceholder } from 'shared/mui/DatePicker/utils/formatRangeInputToPlaceholder';
import { getValidCalendarDate } from 'shared/mui/DatePicker/utils/getValidCalendarDate';
import { setCurrentTime } from 'shared/mui/DatePicker/utils/setCurrentTime';
import { setToday } from 'shared/mui/DatePicker/utils/setToday';
import { formatInputToTimestamp, parseDateInput } from 'utils/date';

export type DateRangePickerValue = number | null;

export type DateTimeRangePickerProps = Omit<
  TextFieldProps,
  'value' | 'onChange' | 'defaultValue'
> & {
  minDate?: Date | string | number;
  maxDate?: Date | string | number;
  value: [DateRangePickerValue, DateRangePickerValue];
  displayShortcuts?: boolean;
  onChange?: (newValue: [DateRangePickerValue, DateRangePickerValue]) => void;
  shortcuts?: Shortcuts;
};

export const DateTimeRangePicker: React.FC<DateTimeRangePickerProps> = ({
  label,
  variant = 'outlined',
  displayShortcuts = true,
  disabled,
  onChange: onChangeProp,
  onBlur: onBlurProp,
  minDate,
  maxDate,
  value,
  shortcuts = DEFAULT_SHORTCUTS,
  size = 'small',
  ...restProps
}) => {
  const translate = useTypedTranslate();
  const { handleClose, handleClick, anchorEl } = useAnchor<HTMLDivElement>();
  const [error, setError] = useState(false);
  const [startTime, setStartTime] = useState<DateRangePickerValue>(value[0]);
  const [endTime, setEndTime] = useState<DateRangePickerValue>(value[1]);

  const [resetValue, setResetValue] = useState<[DateRangePickerValue, DateRangePickerValue]>(value);

  const [inputValue, setInputValue] = useState<string | undefined>(
    formatRangeInputToPlaceholder(value, { format: DEFAULT_DATE_TIME_MASK }),
  );

  const [calendarValue, setCalendarValue] =
    useState<[DateRangePickerValue, DateRangePickerValue]>(value);

  const open = Boolean(anchorEl);
  const id = open ? 'date-time-picker-popover' : undefined;

  useEffect(() => {
    setStartTime(value[0]);
    setEndTime(value[1]);
    setInputValue(formatRangeInputToPlaceholder(value, { format: DEFAULT_DATE_TIME_MASK }));
    setCalendarValue(value);
  }, [value]);

  const parseRangeInput = (text: string): [DateRangePickerValue, DateRangePickerValue] => {
    const [start = '', end = ''] = text.split(DEFAULT_SEPARATOR);
    const [startDate, startClock] = start.trim().split(' ');
    const [endDate, endClock] = end.trim().split(' ');

    const startDay = formatInputToTimestamp(startDate, { format: DEFAULT_DATE_MASK });
    const endDay = formatInputToTimestamp(endDate, { format: DEFAULT_DATE_MASK });

    const startClockValue = parseDateInput(startClock, { format: DEFAULT_TIME_MASK });
    const endClockValue = parseDateInput(endClock, { format: DEFAULT_TIME_MASK });

    const startValue = startDay ? setCurrentTime(startDay, startClockValue || 0) : null;

    const isEndClockFilled = Boolean(endClock) && !endClock.includes('_');
    const endValue = endDay
      ? isEndClockFilled
        ? setCurrentTime(endDay, endClockValue || 0)
        : getTime(endOfDay(endDay))
      : null;

    return [startValue, endValue];
  };

  const debouncedOnChangeInputHandler = useDebounce((target: string) => {
    const [start, end] = target.split(DEFAULT_SEPARATOR);

    if (!start && !end) {
      setError(false);
      setStartTime(null);
      setEndTime(null);
      setCalendarValue([null, null]);
      setInputValue(undefined);
      return;
    }

    const [parsedStart, parsedEnd] = parseRangeInput(target);

    setError(parsedStart === null && parsedEnd === null);
    setStartTime(parsedStart);
    setEndTime(parsedEnd);
    setCalendarValue([parsedStart, parsedEnd]);
    setInputValue(target);
  }, 300);

  function onBlur(event: React.FocusEvent<HTMLInputElement>) {
    onBlurProp?.(event);

    const currentValue = event.target.value;

    if (!currentValue || currentValue === DEFAULT_DATE_TIME_RANGE_PLACEHOLDER) {
      setError(false);
      if (value[0] !== null || value[1] !== null) {
        onChangeProp?.([null, null]);
        setResetValue([null, null]);
      }
      return;
    }

    const [start, end] = parseRangeInput(currentValue);

    if (start === null) {
      setError(true);
      return;
    }

    const next: [DateRangePickerValue, DateRangePickerValue] = [
      start,
      end ?? Number(endOfDay(new Date())),
    ];

    if (next[0] === value[0] && next[1] === value[1]) {
      return;
    }

    setError(false);
    onChangeProp?.(next);
    setResetValue(next);
  }

  function onChangeInput(event: React.ChangeEvent<HTMLInputElement>) {
    debouncedOnChangeInputHandler(event?.target?.value);
  }

  function onChangeCalendar(value: (DateObject | null)[]) {
    const [start, end] = value;

    const validStart = getValidCalendarDate(start, startTime);
    const validEnd = getValidCalendarDate(end, endTime);
    const validEndOfDay = validEnd ? getTime(endOfDay(validEnd)) : null;

    const validValue: [DateRangePickerValue, DateRangePickerValue] = [validStart, validEndOfDay];

    setStartTime(validStart);
    setEndTime(validEndOfDay);

    setInputValue(formatRangeInputToPlaceholder(validValue, { format: DEFAULT_DATE_TIME_MASK }));
    setCalendarValue(validValue);
  }

  const onChangeStartTime = (value: Date) => {
    const [startDate, endDate] = calendarValue;

    if (!isValid(value)) {
      setStartTime(null);
      setCalendarValue((prevValue) => [null, prevValue[1]]);
      setInputValue(
        formatRangeInputToPlaceholder([null, endDate], { format: DEFAULT_DATE_TIME_MASK }),
      );

      return;
    }

    if (!startDate) {
      const newValue = setToday(value);
      setStartTime(newValue);
      setCalendarValue((prevValue) => [newValue, prevValue[1]]);
      setInputValue(
        formatRangeInputToPlaceholder([newValue, endDate], { format: DEFAULT_DATE_TIME_MASK }),
      );

      return;
    }

    const newDateTime = setCurrentTime(startDate, value);
    setStartTime(newDateTime);
    setCalendarValue((prevValue) => [newDateTime, prevValue[1]]);
    setInputValue(
      formatRangeInputToPlaceholder([newDateTime, endDate], { format: DEFAULT_DATE_TIME_MASK }),
    );
  };
  const onChangeEndTime = (value: Date) => {
    const [startDate, endDate] = calendarValue;

    if (!isValid(value)) {
      setEndTime(null);
      setCalendarValue((prevValue) => [prevValue[0], null]);
      setInputValue(
        formatRangeInputToPlaceholder([startDate, null], { format: DEFAULT_DATE_TIME_MASK }),
      );

      return;
    }

    if (!endDate) {
      const newValue = setToday(value);
      setEndTime(newValue);
      setCalendarValue((prevValue) => [prevValue[0], newValue]);
      setInputValue(
        formatRangeInputToPlaceholder([startDate, newValue], { format: DEFAULT_DATE_TIME_MASK }),
      );

      return;
    }

    const newDateTime = setCurrentTime(endDate, value);
    setEndTime(newDateTime);
    setCalendarValue((prevValue) => [prevValue[0], newDateTime]);
    setInputValue(
      formatRangeInputToPlaceholder([startDate, newDateTime], { format: DEFAULT_DATE_TIME_MASK }),
    );
  };

  const onClickShortcut = (value: [DateRangePickerValue, DateRangePickerValue]) => {
    const [startDate, endDate] = value;

    setStartTime(startDate);
    setEndTime(endDate);
    setInputValue(formatRangeInputToPlaceholder(value, { format: DEFAULT_DATE_TIME_MASK }));
    setCalendarValue(value);
  };

  function onClickApply() {
    if (onChangeProp) {
      const validValue: [DateRangePickerValue, DateRangePickerValue] = [
        calendarValue[0],
        calendarValue[1] || Number(endOfDay(new Date())),
      ];
      onChangeProp(validValue);
      setResetValue(validValue);
      handleClose();
    }
  }

  function onClickReset() {
    if (onChangeProp) {
      onChangeProp(resetValue);
      setStartTime(resetValue[0]);
      setEndTime(resetValue[1]);
      setInputValue(formatRangeInputToPlaceholder(resetValue, { format: DEFAULT_DATE_TIME_MASK }));
      setCalendarValue(resetValue);
      handleClose();
    }
  }

  return (
    <>
      <DatePickerMaskInput
        {...restProps}
        disabled={disabled}
        error={error}
        label={label}
        mask={DEFAULT_DATE_TIME_RANGE_MASK}
        onBlur={onBlur}
        onChange={onChangeInput}
        onClick={(e) => {
          if (disabled) {
            return;
          }

          handleClick(e);
        }}
        placeholder={DEFAULT_DATE_TIME_RANGE_PLACEHOLDER}
        size={size}
        value={inputValue}
        variant={variant}
      />
      <Popover
        anchorEl={anchorEl}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        disableAutoFocus={true}
        id={id}
        onClose={handleClose}
        open={open}
        slotProps={{
          paper: {
            sx: {
              maxHeight: '80vh',
              overflow: 'auto',
            },
          },
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
      >
        <PopoverLayoutStyled>
          <PopoverCalendarContainerStyled>
            {displayShortcuts && (
              <PopoverListStyled>
                <DatePickerShortcuts onClick={onClickShortcut} shortcuts={shortcuts} />
              </PopoverListStyled>
            )}
            <PopoverContentStyled>
              <DatePickerToolbar
                end={calendarValue[1]}
                start={calendarValue[0]}
                title={translate('app.datePicker.dateTimeRangeSelection')}
              />
              <PopoverDatesStyled>
                <PopoverCalendarContainerStyled>
                  <Calendar
                    maxDate={maxDate}
                    minDate={minDate}
                    numberOfMonths={2}
                    onChange={onChangeCalendar}
                    range
                    value={calendarValue}
                  />
                </PopoverCalendarContainerStyled>
                <PopoverTimesStyled>
                  <LocalizationProvider dateAdapter={AdapterDateFns}>
                    <DatePickerTime
                      label={translate('app.datePicker.timeFrom')}
                      onChange={onChangeStartTime}
                      value={startTime}
                    />
                    <DatePickerTime
                      label={translate('app.datePicker.timeTo')}
                      onChange={onChangeEndTime}
                      value={endTime}
                    />
                  </LocalizationProvider>
                </PopoverTimesStyled>
              </PopoverDatesStyled>
            </PopoverContentStyled>
          </PopoverCalendarContainerStyled>
          <DatePickerActions>
            <Button onClick={onClickReset} variant="outlined">
              {translate('app.buttons.cancel')}
            </Button>
            <Button onClick={onClickApply} variant="contained">
              {translate('app.buttons.confirm')}
            </Button>
          </DatePickerActions>
        </PopoverLayoutStyled>
      </Popover>
    </>
  );
};
